import React, { useEffect, useState } from 'react'

import { Stack, Typography } from '@mui/material'

import { Alert, Button, Modal } from '@repo/ui'
import { useVoidPendingSale, type PendingSale } from 'hooks/api'
import { useSnackbar } from 'contexts'

interface VoidSaleModalProps {
  sale: PendingSale | null
  open: boolean
  onClose: () => void
  refetch: () => void
}

/**
 * VRG-455 — retract a recorded sale.
 *
 * ## Void, not delete
 *
 * The row survives in a terminal `voided` state with a timestamp, as the audit
 * of a sale that was retracted. Deleting it would destroy the evidence that the
 * assertion was ever made, which is the opposite of what the record is for.
 *
 * ## Why the confirmation is worth the click
 *
 * A pending record is the buyer's right to claim. Voiding it takes that away,
 * and there is no undo: recording the sale again creates a NEW record, which is
 * fine but is not the same row and loses whatever reminder history the original
 * had. The consequence is named rather than left to "are you sure?".
 *
 * ## X, not Trash
 *
 * This is a set-removal by severity, not a record deletion — nothing disappears
 * from the database (§38) — but the row is a labelled menu item, where the verb
 * glyph wins, so the caller uses a ban/cancel glyph rather than either.
 */
export const VoidSaleModal = (props: VoidSaleModalProps) => {
  const { sale, open, onClose, refetch } = props

  const { open: notification } = useSnackbar()
  const voidSale = useVoidPendingSale()
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setError(null)
    voidSale.reset()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sale?.id])

  const handleConfirm = () => {
    if (!sale?.id) return
    setError(null)

    voidSale.mutate(
      { id: sale.id },
      {
        onSuccess: () => {
          notification({
            type: 'success',
            title: `Sale to ${sale.buyer_email} voided`,
          })
          refetch()
          onClose()
        },
        onError: (mutationError: Error) => {
          setError(mutationError?.message || 'Could not void this record')
        },
      },
    )
  }

  return (
    <Modal
      title="Void this sale"
      open={open}
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={voidSale.isPending}>
            Cancel
          </Button>
          {/* Full red fill: the user has already engaged, and this is the point
              of no return for that record (§32 — a confirm in a transient
              surface is always-red, unlike the persistent trigger that opened
              it). */}
          <Button
            variant="primary"
            destructive
            onClick={handleConfirm}
            disabled={voidSale.isPending}
            loading={voidSale.isPending}
          >
            Void sale
          </Button>
        </>
      }
    >
      <Stack spacing={6}>
        <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.6 }}>
          {sale?.product?.title
            ? `${sale.buyer_email} will no longer be able to claim ${sale.product.title} by scanning it.`
            : `${sale?.buyer_email ?? 'This buyer'} will no longer be able to claim this product by scanning it.`}
        </Typography>

        {error && (
          <Alert severity="error" description={error} sx={{ padding: '12px 24px' }} />
        )}

        <Alert
          severity="warning"
          description="The record is kept as history and cannot be reopened. If the sale is still good, record it again to give the buyer a new claim."
          sx={{ padding: '12px 24px' }}
        />
      </Stack>
    </Modal>
  )
}
